import type { JSX } from 'react';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Trash2, Plus } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Select } from '@/components/shared/select';
import type { IdpGroupSegmentMapping, Segment } from '@echo-work-platform/shared-types';

interface GraphGroup {
  id: string;
  displayName: string;
  description?: string;
}

// EW-66: directory listing is an Entra (Graph) capability. `available: false` means the
// deployment has no directory to ask (e.g. Keycloak) — the form then takes free-text entry,
// where the group id is what the IdP's group mapper emits into the token (on Keycloak the
// group NAME).
interface IdpGroupsResponse {
  available: boolean;
  groups: GraphGroup[];
}

export function GroupMappings(): JSX.Element {
  const queryClient = useQueryClient();
  const groups = useQuery({
    queryKey: ['/api/admin/idp-groups'],
    queryFn: () => api.get<IdpGroupsResponse>('/api/admin/idp-groups'),
  });
  const mappings = useQuery({
    queryKey: ['/api/admin/group-mappings'],
    queryFn: () => api.get<IdpGroupSegmentMapping[]>('/api/admin/group-mappings'),
  });
  const segments = useQuery({
    queryKey: ['/api/admin/segments'],
    queryFn: () => api.get<Segment[]>('/api/admin/segments'),
  });
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('');
  // Free-text entry for deployments without a directory (EW-66).
  const [manualId, setManualId] = useState('');
  const [manualName, setManualName] = useState('');

  const directoryAvailable = groups.data?.available ?? true;

  const create = useMutation({
    mutationFn: (v: { idpGroupId: string; idpGroupDisplayName: string; segmentId: string }) =>
      api.post('/api/admin/group-mappings', v),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/group-mappings'] });
      setSelectedGroup('');
      setSelectedSegment('');
      setManualId('');
      setManualName('');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/group-mappings/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/group-mappings'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Group Mappings</h2>
        {directoryAvailable && (
          <button className="btn-secondary" onClick={() => void groups.refetch()}>
            <RefreshCw className="h-4 w-4" /> Címtár frissítése
          </button>
        )}
      </div>

      <div className="card p-4">
        <h3 className="mb-3 text-sm font-semibold">Új mapping</h3>
        <div className={`grid grid-cols-1 gap-3 ${directoryAvailable ? 'sm:grid-cols-3' : 'sm:grid-cols-4'}`}>
          {directoryAvailable ? (
            <div>
              <label className="label">IdP-csoport</label>
              <Select
                value={selectedGroup}
                onChange={setSelectedGroup}
                placeholder="— válassz —"
                options={(groups.data?.groups ?? []).map((g) => ({ value: g.id, label: g.displayName }))}
              />
            </div>
          ) : (
            <>
              <div>
                <label className="label">IdP-csoport azonosítója</label>
                <input
                  className="input w-full"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  placeholder="ahogy a token groups claimjében áll"
                />
              </div>
              <div>
                <label className="label">Megjelenített név</label>
                <input
                  className="input w-full"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="olvasható csoportnév"
                />
              </div>
            </>
          )}
          <div>
            <label className="label">Szegmens</label>
            <Select
              value={selectedSegment}
              onChange={setSelectedSegment}
              placeholder="— válassz —"
              options={(segments.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
            />
          </div>
          <div className="flex items-end">
            <button
              className="btn-primary w-full"
              disabled={
                (directoryAvailable ? !selectedGroup : !manualId.trim() || !manualName.trim()) ||
                !selectedSegment
              }
              onClick={() => {
                if (directoryAvailable) {
                  const g = groups.data?.groups.find((x) => x.id === selectedGroup);
                  if (!g) return;
                  create.mutate({
                    idpGroupId: g.id,
                    idpGroupDisplayName: g.displayName,
                    segmentId: selectedSegment,
                  });
                } else {
                  create.mutate({
                    idpGroupId: manualId.trim(),
                    idpGroupDisplayName: manualName.trim(),
                    segmentId: selectedSegment,
                  });
                }
              }}
            >
              <Plus className="h-4 w-4" /> Mapping hozzáadása
            </button>
          </div>
        </div>
        {!directoryAvailable && (
          <p className="mt-2 text-xs text-slate-500">
            Ez a telepítés nem kérdez le címtárat — a csoport azonosítóját kézzel kell megadni, pontosan úgy,
            ahogy az IdP a token <code>groups</code> claimjébe írja (Keycloaknál a csoport neve).
          </p>
        )}
        {groups.isError && (
          <p className="mt-2 text-xs text-rose-600">Címtár-lekérdezési hiba: {String(groups.error)}</p>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2">IdP-csoport (név)</th>
              <th className="px-4 py-2">IdP-csoport (azonosító)</th>
              <th className="px-4 py-2">Szegmens</th>
              <th className="w-12 px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {mappings.data?.map((m) => {
              const seg = segments.data?.find((s) => s.id === m.segmentId);
              return (
                <tr key={m.id}>
                  <td className="px-4 py-2 font-medium">{m.idpGroupDisplayName}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">
                    {/* Entra object ids are long uuids worth truncating; a Keycloak group NAME is short
                        and truncating it would hide exactly the value the operator typed. */}
                    {m.idpGroupId.length > 16 ? `${m.idpGroupId.slice(0, 8)}…` : m.idpGroupId}
                  </td>
                  <td className="px-4 py-2">{seg?.name ?? m.segmentId.slice(0, 8)}</td>
                  <td className="px-4 py-2">
                    <button
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => remove.mutate(m.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {mappings.data?.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                  Még nincs mapping.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
